"use client";

import React, { useState, useEffect } from 'react';
import styles from './FaqBlock.module.scss';
import Image from 'next/image';

export interface FaqItem {
  question: string;
  answer: string;
}

interface FaqBlockProps {
  title: string;
  items: FaqItem[];
}

const FaqTitle: React.FC<{ title: string }> = ({ title }) => (
  <h2 className={styles.faqTitle}>{title}</h2>
);

const FaqAnswer: React.FC<{ answer: string; open: boolean; id: string }> = ({ answer, open, id }) => (
  <div
    id={id}
    className={styles.faqAnswer}
    style={{
      maxHeight: open ? 500 : 0,
      opacity: open ? 1 : 0,
      transition: 'max-height 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.5s',
      overflow: 'hidden',
      visibility: open ? 'visible' : 'hidden',
    }}
    aria-hidden={!open}
    dangerouslySetInnerHTML={{ __html: answer }}
  />
);

const FaqBlock: React.FC<FaqBlockProps> = ({ title, items }) => {
  const [openIndex, setOpenIndex] = useState(0);

  // Generate FAQ schema for SEO
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'name': 'Frequently Asked Questions',
    'mainEntity': items.map((item) => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  };

  useEffect(() => {
    setOpenIndex(0); // Always open first on mount
  }, [items]);

  return (
    <div className={styles.faqBlockWrapper}>
      <FaqTitle title={title} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        suppressHydrationWarning
      />
      <div className={styles.faqSeparator} />
      <div>
        {items.map((item, idx) => (
          <div
            key={idx}
            className={styles.faqItem + (idx === openIndex ? ' ' + styles.open : '')}
            style={{ borderBottom: idx !== items.length - 1 ? '1px solid rgba(245, 248, 255, 0.12)' : 'none' }}
          >
            <button
              className={styles.faqQuestion}
              onClick={() => setOpenIndex(idx === openIndex ? -1 : idx)}
              aria-expanded={idx === openIndex}
              aria-controls={`faq-answer-${idx}`}
              type="button"
            >
              <span>{item.question}</span>
              <span className={styles.faqIconWrapper}>
                <Image
                  src={idx === openIndex ?  '/tkn_lnd_pg_assets/images/icons/icon-faq-close.svg': '/tkn_lnd_pg_assets/images/icons/icon-faq-open.svg'}
                  alt={idx === openIndex ? 'Collapse' : 'Expand'}
                  width={16}
                  height={16}
                  className={styles.faqIcon}
                  style={{
                    transition: 'opacity 0.3s, transform 0.3s',
                    opacity: idx === openIndex ? 1 : 0.7,
                    transform: idx === openIndex ? 'scale(1)' : 'scale(1)',
                  }}
                />
              </span>
            </button>
            <FaqAnswer answer={item.answer} open={idx === openIndex} id={`faq-answer-${idx}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FaqBlock;
